import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box, useToggle } from '@factorialco/deprecated-design-system'
import { OneDataCollection, useDataCollectionData } from '@factorialco/f0-react/dist/experimental'
import { List } from '@factorialco/f0-react/icons/app'
import { observer } from 'mobx-react'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import type Employee from 'modules/employees/store/models/Employee'
import SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import { ExpensablesProvider } from 'modules/payroll_bundle/components/Supplements/expensablesContext'
import { SupplementExtraDataProvider } from 'modules/payroll_bundle/components/Supplements/supplementExtraDataContext'
import { useSupplementsSalariesDataCollectionSource } from 'modules/payroll_bundle/components/Supplements/useSupplementsSalariesDataCollectionSource'
import useFetchSupplementsBatchByPeriod from 'modules/payroll_bundle/components/Supplements/useFetchSupplementsBatchByPeriod'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { usePayrollContextlessPolicies } from 'modules/payroll_bundle/hooks/usePayrollContextlessPolicies'
import { useBulkImportSupplements } from 'modules/payroll_bundle/lib/bulkImportSupplementsContext'
import {
  SHOW_MANAGED_IMPORTED_MODAL,
  SHOW_UPLOAD_MODAL,
} from 'modules/payroll_bundle/lib/constants'
import type { RouteContextParams } from 'modules/payroll_bundle/lib/RouteContextParams'
import { useFrequency } from 'modules/payroll_bundle/lib/frequencyContext'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { StatusAuthorizationProvider } from 'modules/payroll_bundle/lib/statusAuthorizationsContext'
import { SupplementsInsightsContextProvider } from 'modules/payroll_bundle/pages/SupplementsPage/supplementsInsightsContext'
import SupplementsInsights from 'modules/payroll_bundle/pages/SupplementsPage/SupplementsInsights'
import DeleteModal from 'modules/payroll_bundle/pages/SupplementsPage/BulkImport/_components/DeleteModal'
import TaxonomyForm from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/List/Line/Form/CompensationTypePopoverMenu/TaxonomyForm'
import type PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'
import supplementBulkImportCollection from 'modules/payroll_bundle/store/collections/supplementBulkImports'
import {
  ContractTaxonomyFragment,
  SearchableSupplementsLegalEntityFragment,
  SupplementsDataDocument,
} from 'generated/resources/graphql'
import { useExpensables } from 'modules/payroll_bundle/components/Supplements/expensablesContext'
import { PayrollStatus } from 'modules/payroll_bundle/lib/enums'
import { useStatusAuthorizations } from 'modules/payroll_bundle/lib/statusAuthorizationsContext'

import CollaborateWidget from 'modules/payroll_bundle/components/Supplements/components/Table/CollaborateWidget'

import { SupplementsCustomVisualization } from './SupplementsCustomVisualization'

interface SupplementsProps {
  policyPeriod?: PolicyPeriod | null
  bulkImportSupplementStage?: string
  itemToHighlight?: { employeeId: string } | null
}

export const SupplementsByPeriod = observer((props: SupplementsProps) => {
  const supplementsStore = useLazyRef<SupplementsFormStore>(
    () => new SupplementsFormStore()
  ).current

  const { legalEntities } = useCurrentSession().company
  const [openTaxonomyCreationModal, toggleTaxonomyCreationModal] = useToggle(false)
  const handleTaxonomyCreationModal = (newState: boolean) => {
    toggleTaxonomyCreationModal(newState)()
  }
  const [employeeExpansionState, setEmployeeExpansionState] = useState<Record<string, boolean>>({})

  const { date, frequency } = useFrequency()
  const { currentPolicyPeriods, periodIndex } = usePolicyPeriods()
  const { filteredEmployees: employeesInPeriod } = useOutletContext<RouteContextParams>()

  const employeesInPeriodKey = employeesInPeriod.map((e) => e.id).join(',')
  const visibleEmployees = useMemo(
    () =>
      employeesInPeriod.filter(
        (employee) =>
          policyAuthorizations.for('payroll_supplements.supplements.read', employee) ||
          policyAuthorizations.for('payroll_supplements.observations.read', employee)
      ),
    [employeesInPeriodKey]
  )
  const visibleEmployeesKey = visibleEmployees.map((e) => e.id).join(',')
  const employeesById = useMemo(
    () => Object.fromEntries(visibleEmployees.map((e) => [e.id, e])),
    [visibleEmployeesKey]
  )

  const policyPeriodId = currentPolicyPeriods[periodIndex]?.id
  const policyPeriod = currentPolicyPeriods[periodIndex]

  // Use the new salaries-based data collection source that filters by policy period
  const source = useSupplementsSalariesDataCollectionSource({ policyPeriodId })
  const { data: collectionData } = useDataCollectionData(source)
  const records = collectionData?.records ?? []
  const recordsKey = records.map((r) => (r as { employeeId: string }).employeeId).join(',')

  const employeeIds = useMemo(
    () => records.map((r) => (r as { employeeId: string }).employeeId),
    [recordsKey]
  )

  const { loading } = useFetchSupplementsBatchByPeriod({
    policyPeriodId,
    supplementsStore,
    employeeIds,
  })

  const { data, loading: loadingData } = useQuery(SupplementsDataDocument)
  const taxonomies = data?.contracts.taxonomies ?? []
  const { showDeleteModal } = useBulkImportSupplements()
  const { loading: loadingExpensables } = useExpensables()
  const { loading: loadingPayrollContextlessPolicies, canImportSupplements } =
    usePayrollContextlessPolicies()
  const canSeeSupplements = policyAuthorizations.for('payroll_supplements.supplements.read')

  const policyPeriodStatus = policyPeriod?.attributes?.status
  const currentPolicyPeriodStatus = currentPolicyPeriods[periodIndex]?.get('status')
  const allowedStatuses: PayrollStatus[] = [
    PayrollStatus.PREPARATION,
    PayrollStatus.SUPPLEMENTS_DEFINITION,
  ]
  const isEditableStatus = allowedStatuses.includes(currentPolicyPeriodStatus)
  const canChangeStatus = data?.payroll._policies?.periodChangePayrollPeriodStatus?.can ?? false

  const toggleEmployeeExpansion = (id: string) => {
    setEmployeeExpansionState((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const handleExpandAll = useMemo(
    () => () => {
      setEmployeeExpansionState((prev) => {
        const next = { ...prev }
        records.forEach((r) => {
          next[String((r as { employeeId: string }).employeeId)] = true
        })
        return next
      })
    },
    [recordsKey]
  )

  const handleCollapseAll = useMemo(
    () => () => {
      setEmployeeExpansionState((prev) => {
        const next = { ...prev }
        records.forEach((r) => {
          next[String((r as { employeeId: string }).employeeId)] = false
        })
        return next
      })
    },
    [recordsKey]
  )

  const allExpanded =
    records.length > 0 &&
    records.every((r) => employeeExpansionState[String((r as { employeeId: string }).employeeId)])

  // Salary data comes from the records directly, no separate query needed
  const loadingSupplements =
    loading || loadingExpensables || loadingPayrollContextlessPolicies || loadingData

  return (
    <SupplementsInsightsContextProvider>
      <StatusAuthorizationProvider
        employees={employeesInPeriod}
        policyPeriod={policyPeriod}
        policyPeriodStatus={policyPeriod?.attributes?.status}
      >
        <SupplementsByPeriodContent
          supplementsStore={supplementsStore}
          source={source}
          loading={loading}
          loadingSupplements={loadingSupplements}
          taxonomies={taxonomies}
          canSeeSupplements={canSeeSupplements}
          canImportSupplements={canImportSupplements}
          canChangeStatus={canChangeStatus}
          showDeleteModal={showDeleteModal}
          legalEntities={legalEntities}
          openTaxonomyCreationModal={openTaxonomyCreationModal}
          handleTaxonomyCreationModal={handleTaxonomyCreationModal}
          date={date}
          frequency={frequency}
          policyPeriodId={policyPeriodId}
          policyPeriodStatus={policyPeriodStatus}
          isEditableStatus={isEditableStatus}
          itemToHighlight={props.itemToHighlight}
          employeesById={employeesById}
          employeeExpansionState={employeeExpansionState}
          toggleEmployeeExpansion={toggleEmployeeExpansion}
          onExpandAll={handleExpandAll}
          onCollapseAll={handleCollapseAll}
          allExpanded={allExpanded}
        />
      </StatusAuthorizationProvider>
    </SupplementsInsightsContextProvider>
  )
})

interface SupplementsByPeriodContentProps {
  supplementsStore: SupplementsFormStore
  source: ReturnType<typeof useSupplementsSalariesDataCollectionSource>
  loading: boolean
  loadingSupplements: boolean
  taxonomies: ContractTaxonomyFragment[]
  canSeeSupplements: boolean
  canImportSupplements: boolean
  canChangeStatus: boolean
  showDeleteModal: boolean
  legalEntities: SearchableSupplementsLegalEntityFragment[]
  openTaxonomyCreationModal: boolean
  handleTaxonomyCreationModal: (value: boolean) => void
  date: ReturnType<typeof useFrequency>['date']
  frequency: string
  policyPeriodId: string | null | undefined
  policyPeriodStatus?: PayrollStatus
  isEditableStatus: boolean
  itemToHighlight?: { employeeId: string } | null
  employeesById: Record<string, Employee>
  employeeExpansionState: Record<string, boolean>
  toggleEmployeeExpansion: (id: string) => void
  onExpandAll: () => void
  onCollapseAll: () => void
  allExpanded: boolean
}

const SupplementsByPeriodContent = observer((contentProps: SupplementsByPeriodContentProps) => {
  const { policyPeriodAuthorizations } = useStatusAuthorizations()
  const canEditSupplementsInCurrentStatus = policyPeriodAuthorizations.edit_supplements
  const showCollaborateWidget = canEditSupplementsInCurrentStatus || contentProps.canChangeStatus
  const { supplementDispatch } = useBulkImportSupplements()
  const importDisabledReason = useMemo(() => {
    if (contentProps.loading) return i18n.t('common.please_wait')
    if (!contentProps.isEditableStatus) {
      return i18n.t('payroll_bundle.import_supplements.import.disabled.invalid_status', {
        phase: i18n.t('payroll_bundle.policy_period.status.phases.supplements_definition'),
      })
    }
    if (!canEditSupplementsInCurrentStatus) {
      return i18n.t('payroll_bundle.import_supplements.import.disabled.collaboration_off')
    }
    return null
  }, [contentProps.loading, contentProps.isEditableStatus, canEditSupplementsInCurrentStatus])

  const didImportSupplements = supplementBulkImportCollection.length > 0
  const sourceWithActions = useUnsafeMemo(() => {
    const expandCollapseAction = contentProps.allExpanded
      ? {
          label: i18n.t('payroll_bundle.supplements.supplement_collapse_all'),
          onClick: contentProps.onCollapseAll,
        }
      : {
          label: i18n.t('payroll_bundle.supplements.supplement_expand_all'),
          onClick: contentProps.onExpandAll,
        }
    const secondaryActionsList = [
      expandCollapseAction,
      ...(contentProps.canImportSupplements
        ? [
            {
              label: i18n.t('payroll_bundle.manage_imported_supplements.title'),
              onClick: () => supplementDispatch({ type: SHOW_MANAGED_IMPORTED_MODAL }),
              disabled: !didImportSupplements,
            },
          ]
        : []),
    ]
    const primaryActions = contentProps.canImportSupplements
      ? () => [
          {
            label: i18n.t('payroll_bundle.import_supplements.import.import_supplements'),
            onClick: () => {
              supplementDispatch({ type: SHOW_UPLOAD_MODAL })
              track(trackingEvents.payroll.userClicksOnPayrollImportSupplements)
            },
            disabled: !!importDisabledReason,
          },
        ]
      : undefined
    // Explicitly preserve mappedVariables and document to maintain GraphqlDataCollectionSource type
    return {
      ...contentProps.source,
      mappedVariables: contentProps.source.mappedVariables,
      document: contentProps.source.document,
      primaryActions,
      secondaryActions: () => secondaryActionsList,
    }
  }, [
    contentProps.source,
    contentProps.canImportSupplements,
    contentProps.onExpandAll,
    contentProps.onCollapseAll,
    contentProps.allExpanded,
    importDisabledReason,
    didImportSupplements,
    supplementDispatch,
  ])

  return (
    <ExpensablesProvider supplementsStore={contentProps.supplementsStore}>
      <ErrorBoundary>
        {showCollaborateWidget && (
          <Box width="full" justifyContent="spaceBetween" flexDirection="row" paddingBottom="s8">
            <CollaborateWidget
              status={contentProps.policyPeriodStatus}
              readonly={!contentProps.canChangeStatus}
            />
          </Box>
        )}
        <SupplementExtraDataProvider
          supplementsStore={contentProps.supplementsStore}
          loadingSupplementData={contentProps.loading}
          date={contentProps.date}
          frequency={contentProps.frequency}
          policyPeriodId={contentProps.policyPeriodId}
        >
          {contentProps.canSeeSupplements && contentProps.supplementsStore.forms.length > 0 && (
            <SupplementsInsights supplementsStore={contentProps.supplementsStore} />
          )}
          <OneDataCollection
            id="payroll/supplements/employees/v2"
            source={sourceWithActions}
            visualizations={[
              {
                type: 'custom',
                label: i18n.t('payroll_bundle.supplements.title'),
                icon: List,
                component: () => (
                  <SupplementsCustomVisualization
                    source={contentProps.source}
                    employeesById={contentProps.employeesById}
                    supplementsStore={contentProps.supplementsStore}
                    loadingSupplements={contentProps.loadingSupplements}
                    taxonomies={contentProps.taxonomies}
                    itemToHighlight={contentProps.itemToHighlight}
                    employeeExpansionState={contentProps.employeeExpansionState}
                    toggleEmployeeExpansion={contentProps.toggleEmployeeExpansion}
                    toggleTaxonomyCreationModal={contentProps.handleTaxonomyCreationModal}
                  />
                ),
              },
            ]}
          />
          {contentProps.canImportSupplements && contentProps.showDeleteModal && (
            <DeleteModal store={contentProps.supplementsStore} />
          )}
          {contentProps.openTaxonomyCreationModal && (
            <TaxonomyForm
              legalEntities={contentProps.legalEntities}
              setModalOpen={contentProps.handleTaxonomyCreationModal}
            />
          )}
        </SupplementExtraDataProvider>
      </ErrorBoundary>
    </ExpensablesProvider>
  )
})
